// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// The pre-mic explainer: a calm sheet that tells the patient why we need the microphone
// before the browser prompt fires. Shares the orb overlay's deep-navy palette. Escape and
// the backdrop dismiss it; the primary action takes focus so keyboard users land on it.

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AudioLines, MicOff, X } from 'lucide-react'

interface MicPermissionModalProps {
  denied: boolean
  onAllow: () => void
  onDismiss: () => void
}

export function MicPermissionModal({ denied, onAllow, onDismiss }: MicPermissionModalProps) {
  const { t } = useTranslation()
  const primaryRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    primaryRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mic-explainer-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onDismiss} />

      <div className="relative w-full max-w-sm animate-modal-up rounded-3xl border border-white/10 bg-[#011829] px-7 py-8 shadow-2xl overflow-hidden">
        {/* Ambient depth, matching the voice overlay. */}
        <div className="absolute -top-16 -right-12 w-44 h-44 rounded-full bg-[#006EF9]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-10 w-36 h-36 rounded-full bg-[#02C7C3]/8 blur-3xl pointer-events-none" />

        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label={t('voice.cancel')}
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative flex justify-center mb-6">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              denied
                ? 'bg-gradient-to-br from-red-500/25 to-red-700/25 border border-red-500/40 text-red-300'
                : 'bg-gradient-to-br from-[#006EF9] to-[#02C7C3] text-white shadow-lg shadow-[#006EF9]/25'
            }`}
          >
            {denied ? <MicOff className="w-7 h-7" /> : <AudioLines className="w-7 h-7" />}
          </div>
        </div>

        <h2 id="mic-explainer-title" className="relative text-[20px] font-bold text-white text-center mb-3 leading-tight">
          {denied ? t('voice.deniedHeadline') : t('voice.allowHeadline')}
        </h2>
        <p className="relative text-[13.5px] text-white/60 text-center leading-relaxed mb-7">
          {denied ? t('voice.deniedBody') : t('voice.allowBody')}
        </p>

        <div className="relative flex flex-col gap-3">
          {!denied && (
            <button
              ref={primaryRef}
              onClick={onAllow}
              className="w-full h-12 rounded-2xl font-semibold text-[15px] text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 active:scale-[0.98] transition-all duration-150 shadow-lg shadow-[#006EF9]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              {t('voice.allowButton')}
            </button>
          )}
          <button
            ref={denied ? primaryRef : undefined}
            onClick={onDismiss}
            className="w-full h-11 rounded-2xl font-medium text-[14px] text-white/70 bg-white/8 border border-white/12 hover:bg-white/12 hover:text-white active:scale-[0.98] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          >
            {t('voice.useTextInstead')}
          </button>
        </div>

        {!denied && (
          <p className="relative text-[11px] text-white/35 text-center mt-5 leading-relaxed">
            {t('voice.hipaaNote')}
          </p>
        )}
      </div>
    </div>
  )
}
